import { useState, useRef, useCallback, useEffect } from "react";
import { Mic, MicOff, Loader2, X, Check, Volume2, Send, RotateCcw, Sunrise } from "lucide-react";
import { Button } from "./ui/button";
import api from "../lib/api";
import { useLocation } from "react-router-dom";

const STATES = { IDLE: "idle", RECORDING: "recording", TRANSCRIBING: "transcribing", PROCESSING: "processing", RESULT: "result" };

const INTENT_LABELS = {
  query: { label: "Query", color: "bg-blue-500/10 text-blue-600" },
  production_log: { label: "Production Log", color: "bg-amber-500/10 text-amber-600" },
  create_task: { label: "Task Created", color: "bg-green-500/10 text-green-600" },
  qc_inspection: { label: "QC Inspection", color: "bg-purple-500/10 text-purple-600" },
  inventory_count: { label: "Stock Count", color: "bg-cyan-500/10 text-cyan-600" },
  meeting_note: { label: "CRM Lead", color: "bg-pink-500/10 text-pink-600" },
  dispatch_update: { label: "Dispatch", color: "bg-orange-500/10 text-orange-600" },
  create_quotation: { label: "Quotation", color: "bg-indigo-500/10 text-indigo-600" },
  machine_status: { label: "Maintenance", color: "bg-red-500/10 text-red-600" },
  unknown: { label: "Unknown", color: "bg-muted text-muted-foreground" },
};

export default function VoiceCommand() {
  const [state, setState] = useState(STATES.IDLE);
  const [transcript, setTranscript] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [briefing, setBriefing] = useState(null);
  const [briefingLoading, setBriefingLoading] = useState(false);
  const mediaRecorder = useRef(null);
  const audioChunks = useRef([]);
  const timerRef = useRef(null);
  const location = useLocation();

  const cleanup = useCallback(() => {
    if (mediaRecorder.current && mediaRecorder.current.state !== "inactive") mediaRecorder.current.stop();
    if (timerRef.current) clearInterval(timerRef.current);
    mediaRecorder.current = null;
    audioChunks.current = [];
  }, []);

  const reset = useCallback(() => {
    cleanup(); setState(STATES.IDLE); setTranscript(""); setResult(null); setError(""); setRecordingTime(0); setBriefing(null);
  }, [cleanup]);

  const close = useCallback(() => {
    cleanup(); setOpen(false); setState(STATES.IDLE); setTranscript(""); setResult(null); setError(""); setRecordingTime(0); setBriefing(null);
  }, [cleanup]);

  useEffect(() => {
    const handler = (e) => {
      if (e.altKey && e.key === "v") { e.preventDefault(); open ? close() : setOpen(true); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, close]);

  const startRecording = async () => {
    try {
      setError("");
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      audioChunks.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunks.current.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        if (timerRef.current) clearInterval(timerRef.current);
        const blob = new Blob(audioChunks.current, { type: "audio/webm" });
        await processAudio(blob);
      };
      mediaRecorder.current = recorder;
      recorder.start();
      setState(STATES.RECORDING);
      setRecordingTime(0);
      timerRef.current = setInterval(() => setRecordingTime((t) => t + 1), 1000);
    } catch {
      setError("Microphone access denied. Please allow microphone in browser settings.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorder.current?.state === "recording") { mediaRecorder.current.stop(); setState(STATES.TRANSCRIBING); }
  };

  const processAudio = async (blob) => {
    setState(STATES.TRANSCRIBING);
    try {
      const formData = new FormData();
      formData.append("audio", blob, "voice.webm");
      const res = await api.post("/voice/transcribe", formData, { headers: { "Content-Type": "multipart/form-data" } });
      if (res.data.error) { setError(res.data.error); setState(STATES.IDLE); return; }
      setTranscript(res.data.text);
      setState(STATES.PROCESSING);
      const cmdRes = await api.post("/voice/command", { text: res.data.text, context_page: location.pathname });
      setResult(cmdRes.data);
      setState(STATES.RESULT);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Voice processing failed");
      setState(STATES.IDLE);
    }
  };

  const sendText = async () => {
    if (!transcript.trim()) return;
    setState(STATES.PROCESSING); setError("");
    try {
      const cmdRes = await api.post("/voice/command", { text: transcript, context_page: location.pathname });
      setResult(cmdRes.data); setState(STATES.RESULT);
    } catch { setError("Command processing failed."); setState(STATES.IDLE); }
  };

  const confirmAction = async (endpoint, payload, field) => {
    try {
      setState(STATES.PROCESSING);
      const res = await api.post(endpoint, payload);
      setResult((prev) => ({ ...prev, executed: true, needs_confirmation: false, summary: res.data.message || "Saved!" }));
      setState(STATES.RESULT);
    } catch { setError(`Failed to save ${field}.`); setState(STATES.RESULT); }
  };

  const fetchBriefing = async () => {
    setBriefingLoading(true);
    try {
      const res = await api.get("/voice/daily-briefing");
      setBriefing(res.data.briefing);
    } catch { setBriefing("Could not generate briefing. Please try again."); }
    setBriefingLoading(false);
  };

  if (!open) {
    return (
      <Button variant="ghost" size="icon" className="h-9 w-9 rounded-md relative group" onClick={() => setOpen(true)}
        title="Voice Command (Alt+V)" data-testid="voice-command-btn">
        <Mic className="w-4 h-4" />
        <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-background" />
      </Button>
    );
  }

  const intentInfo = INTENT_LABELS[result?.intent] || INTENT_LABELS.unknown;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50" onClick={close} data-testid="voice-backdrop" />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[440px] max-w-[92vw] max-h-[80vh] bg-card border rounded-xl shadow-2xl overflow-hidden flex flex-col" data-testid="voice-command-modal">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30 shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
              <Volume2 className="w-3.5 h-3.5 text-primary" />
            </div>
            <span className="text-sm font-semibold">Voice Command</span>
            <kbd className="text-[10px] px-1.5 py-0.5 rounded border bg-muted font-mono text-muted-foreground">Alt+V</kbd>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" className="h-7 w-7 rounded-md" onClick={fetchBriefing}
              title="Morning Briefing" data-testid="voice-briefing-btn" disabled={briefingLoading}>
              {briefingLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sunrise className="w-3.5 h-3.5" />}
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7 rounded-md" onClick={close} data-testid="voice-close-btn">
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4 min-h-[180px] overflow-y-auto flex-1">
          {error && <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm" data-testid="voice-error">{error}</div>}

          {/* Daily Briefing */}
          {briefing && !result && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Sunrise className="w-4 h-4 text-amber-500" />
                <span className="text-sm font-semibold">Morning Briefing</span>
              </div>
              <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-500/10 text-sm leading-relaxed whitespace-pre-line" data-testid="voice-briefing">{briefing}</div>
            </div>
          )}

          {/* Idle / Recording */}
          {(state === STATES.IDLE || state === STATES.RECORDING) && !briefing && (
            <div className="flex flex-col items-center gap-4">
              <p className="text-sm text-muted-foreground text-center">
                {state === STATES.IDLE ? "Tap the mic and speak your command" : `Recording... ${recordingTime}s`}
              </p>
              <button onClick={state === STATES.IDLE ? startRecording : stopRecording} data-testid="voice-mic-btn"
                className={`w-20 h-20 rounded-full flex items-center justify-center transition-all ${
                  state === STATES.RECORDING
                    ? "bg-red-500 text-white shadow-lg shadow-red-500/30 animate-pulse"
                    : "bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg"
                }`}>
                {state === STATES.RECORDING ? <MicOff className="w-8 h-8" /> : <Mic className="w-8 h-8" />}
              </button>
              <div className="text-xs text-muted-foreground text-center space-y-0.5">
                <p>Production: <span className="text-foreground/80">"JO-0001, output 450 kg, waste 12 kg"</span></p>
                <p>Query: <span className="text-foreground/80">"What's today's production?"</span></p>
                <p>Task: <span className="text-foreground/80">"Create task: calibrate QC instruments"</span></p>
                <p>QC: <span className="text-foreground/80">"OD 60.3mm, wall 5.2mm, hardness 82"</span></p>
                <p>CRM: <span className="text-foreground/80">"Meeting with Reliance, 25 lakhs"</span></p>
              </div>
            </div>
          )}

          {/* Transcribing / Processing */}
          {(state === STATES.TRANSCRIBING || state === STATES.PROCESSING) && (
            <div className="flex flex-col items-center gap-3 py-4">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">{state === STATES.TRANSCRIBING ? "Transcribing..." : "Processing..."}</p>
              {transcript && <p className="text-xs text-muted-foreground/70 italic max-w-[320px] text-center">"{transcript}"</p>}
            </div>
          )}

          {/* Result */}
          {state === STATES.RESULT && result && (
            <div className="space-y-3">
              {/* Transcript */}
              <div className="flex items-start gap-2">
                <Mic className="w-3.5 h-3.5 text-muted-foreground mt-1 shrink-0" />
                <div><p className="text-xs text-muted-foreground mb-0.5">You said:</p>
                  <p className="text-sm font-medium" data-testid="voice-transcript">"{transcript}"</p></div>
              </div>
              {/* Intent badge */}
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${intentInfo.color}`} data-testid="voice-intent-badge">{intentInfo.label}</span>
                {result.executed && <Check className="w-3.5 h-3.5 text-green-500" />}
              </div>
              {/* Response / Summary */}
              <div className="p-3 rounded-lg bg-muted/50 text-sm leading-relaxed" data-testid="voice-response">{result.response || result.summary}</div>

              {/* Confirmation UI for production_log */}
              {result.intent === "production_log" && result.needs_confirmation && !result.executed && (
                <ConfirmCard data={result.data}
                  fields={[["jo_number", "Job Order"], ["stage", "Stage"], ["qty_out_kg", "Output (kg)"], ["waste_kg", "Waste (kg)"], ["machine_name", "Machine"], ["operator_name", "Operator"]]}
                  onConfirm={() => confirmAction("/voice/confirm-production", { entry: result.data }, "production")}
                  onCancel={reset} confirmTestId="voice-confirm-production" />
              )}

              {/* Confirmation UI for QC inspection */}
              {result.intent === "qc_inspection" && result.needs_confirmation && !result.executed && (
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <DataField label="Reference" value={result.data?.reference_number} />
                    <DataField label="Type" value={result.data?.inspection_type} />
                    <DataField label="Verdict" value={result.data?.verdict} />
                  </div>
                  {result.data?.parameters?.length > 0 && (
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">Parameters:</p>
                      {result.data.parameters.map((p, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs p-1.5 rounded bg-muted/30">
                          <span className="font-medium flex-1">{p.name}</span>
                          <span>{p.actual_value}</span>
                          <span className={p.result === "pass" ? "text-green-600" : p.result === "fail" ? "text-red-600" : "text-muted-foreground"}>{p.result}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button size="sm" className="flex-1 h-8 gap-1.5" onClick={() => confirmAction("/voice/confirm-qc", { inspection: result.data }, "QC")} data-testid="voice-confirm-qc">
                      <Check className="w-3.5 h-3.5" /> Confirm & Save
                    </Button>
                    <Button size="sm" variant="outline" className="h-8" onClick={reset}>Cancel</Button>
                  </div>
                </div>
              )}

              {/* Data cards for executed intents */}
              {result.executed && result.data && result.intent === "create_task" && (
                <DataCard fields={[["title", "Title"], ["priority", "Priority"]]} data={result.data} />
              )}
              {result.executed && result.data && result.intent === "meeting_note" && (
                <DataCard fields={[["company", "Company"], ["follow_up", "Follow-up"]]} data={result.data} />
              )}
              {result.executed && result.data && result.intent === "create_quotation" && (
                <DataCard fields={[["doc_number", "Quotation"], ["customer", "Customer"], ["total", "Total (Rs)"]]} data={result.data} />
              )}
              {result.executed && result.data && result.intent === "machine_status" && (
                <DataCard fields={[["mr_number", "MR Number"], ["machine", "Machine"], ["priority", "Priority"]]} data={result.data} />
              )}
              {result.executed && result.data && result.intent === "inventory_count" && (
                <DataCard fields={[["heat_number", "Heat No"], ["quantity_kg", "Qty (kg)"], ["warehouse", "Warehouse"]]} data={result.data} />
              )}
            </div>
          )}
        </div>

        {/* Footer: Edit & resend */}
        {state === STATES.RESULT && (
          <div className="flex items-center gap-2 px-5 pb-4 pt-2 border-t shrink-0">
            <input type="text" value={transcript} onChange={(e) => setTranscript(e.target.value)}
              placeholder="Edit and re-send..." className="flex-1 h-8 px-3 text-sm rounded-md border bg-background"
              data-testid="voice-edit-input" onKeyDown={(e) => { if (e.key === "Enter") sendText(); }} />
            <Button size="icon" className="h-8 w-8" onClick={sendText} title="Re-send" data-testid="voice-resend-btn"><Send className="w-3.5 h-3.5" /></Button>
            <Button size="icon" variant="outline" className="h-8 w-8" onClick={reset} title="New command" data-testid="voice-new-btn"><RotateCcw className="w-3.5 h-3.5" /></Button>
          </div>
        )}
      </div>
    </>
  );
}

function DataField({ label, value }) {
  if (!value) return null;
  return <div><span className="text-muted-foreground">{label}:</span> <span className="font-medium">{value}</span></div>;
}

function DataCard({ fields, data }) {
  return (
    <div className="grid grid-cols-2 gap-2 text-xs p-2 rounded-lg border">
      {fields.map(([key, label]) => <DataField key={key} label={label} value={data?.[key]?.toLocaleString?.() || data?.[key]} />)}
    </div>
  );
}

function ConfirmCard({ data, fields, onConfirm, onCancel, confirmTestId }) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2 text-xs">
        {fields.map(([key, label]) => <DataField key={key} label={label} value={data?.[key]} />)}
      </div>
      <div className="flex gap-2">
        <Button size="sm" className="flex-1 h-8 gap-1.5" onClick={onConfirm} data-testid={confirmTestId}>
          <Check className="w-3.5 h-3.5" /> Confirm & Save
        </Button>
        <Button size="sm" variant="outline" className="h-8" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}
